import { create } from 'zustand';
import mentionService from '@/services/mentionService';
import type { NotificationSummary } from '@/types/mentions';

interface NotificationsState {
  // Notification data
  summary: NotificationSummary;
  isLoading: boolean;
  lastFetch: number;
  
  // Actions
  fetchSummary: () => Promise<void>;
  incrementUnread: (isEmergency: boolean) => void;
  decrementUnread: (count: number) => void;
  markChannelSeen: () => void;
  syncAfterReconnect: () => Promise<void>;
  triggerRefresh: () => void;
  reset: () => void;
}

const initialSummary: NotificationSummary = {
  unreadCount: 0,
  hasEmergency: false,
  channelCount: 0,
};

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  // Initial state
  summary: initialSummary,
  isLoading: false,
  lastFetch: 0,

  // Fetch notification summary from API
  fetchSummary: async () => {
    const workspaceId = localStorage.getItem('workspaceId');
    if (!workspaceId) {
      set({ summary: initialSummary });
      return;
    }

    set({ isLoading: true });
    try {
      const data = await mentionService.getSummary();
      set({ 
        summary: data, 
        lastFetch: Date.now(),
        isLoading: false 
      });
    } catch (error) {
      console.error('Failed to fetch notification summary:', error);
      set({ isLoading: false });
    }
  },

  // Increment unread count locally when MentionReceived event fires
  incrementUnread: (isEmergency: boolean) => {
    const current = get().summary;
    set({
      summary: {
        unreadCount: current.unreadCount + 1,
        hasEmergency: current.hasEmergency || isEmergency,
        channelCount: current.channelCount, // Can't accurately track without full state
      },
    });
  },

  // Decrement unread count when mentions are marked as seen
  decrementUnread: (count: number) => {
    const current = get().summary;
    set({
      summary: {
        unreadCount: Math.max(0, current.unreadCount - count),
        hasEmergency: false, // Reset emergency flag when marking seen
        channelCount: Math.max(0, current.channelCount - 1),
      },
    });
  },

  // Mark a specific channel as seen
  markChannelSeen: () => {
    // After marking as seen, refresh to get accurate count
    get().fetchSummary();
  },

  // Sync state after SignalR reconnect
  syncAfterReconnect: async () => {
    await get().fetchSummary();
  },

  // Trigger an immediate refresh (called when SignalR mention received)
  triggerRefresh: () => {
    get().fetchSummary();
  },

  // Reset state (called on workspace change)
  reset: () => {
    set({
      summary: initialSummary,
      isLoading: false,
      lastFetch: 0,
    });
  },
}));
